import { FC, useState, useRef, useEffect, useContext, useCallback, useMemo, type JSX } from "react";
import { Stock } from "@/models/stock";
import { classNames, priceDiffPercentage } from "@/lib/utils";
import { Typography } from "@mui/material";
import { getPeerAverageSeries, getPeersNote, getStockPeers } from "@/controllers/stock-peers";
import { getChartDataNew } from "@/controllers/chart-data";
import type { PeerRow, PeersMeta } from "@/models/stock-peers";
import type { ListQueryParams } from "@/models/table";
import { useChartSeriesToggle } from "src/hooks/useChartSeriesToggle";
import Module from "@/components/module/Module";
import styles from "./StockPeers.module.scss";
import { useAtomValue } from "jotai";
import { backgroundColorState, lighterBackgroundColorState, textColorState, themeState, userState } from "@/lib/store";
import CorrelatedPeersChart from "../stock-correlated-peers/CorrelatedPeersChart";
import type { TimeSeries } from "@/components/ortex-charts/TimeSeriesChart";
import { DataGrid } from "../data-grid/DataGrid";
import { usExchanges } from "@/lib/vars";
import { Context } from "@/lib/Context";
import ReactMarkdown from "react-markdown";
import { markdownHeadingComponents } from "@/lib/markdownHeadings";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ClampedNote from "@/components/clamped-note/ClampedNote";

/** A peer's closing-price line for the chart, keyed by its trading item id. */
const loadPeerLine = async (tradingitemId: number, ticker: string | undefined): Promise<TimeSeries[]> => {
    const rows = await getChartDataNew(tradingitemId, "ohlcv");
    const key = String(tradingitemId);
    return [{ id: key, title: String(ticker ?? key), type: "line" as const, data: (rows ?? []).map((r) => [r[0], r[4]] as [number, number]), precision: 2 }];
};

/** The pinned stock and peer-average rows: neither is a peer, so neither gets a Show on Chart line. */
const isPinnedRow = (row: any) => row?.rowKind === "stock" || row?.rowKind === "average";

/** The chart toggle key of the peer-average row: it has no trading item of its own. */
const AVERAGE_KEY = "peer-average";

interface PropsTypes {
    stock: Stock;
}

/** The peer set the grid shows: business peers (the companies' closest listed competitors, TED-0018). */
const PEER_SOURCE = "business" as const;

/**
 * The chart sits to the right of the list when the list still gets this much width at 75% of the
 * row: every column visible without crowding. Narrower, the chart goes under the list.
 */
const GRID_MIN_WIDTH = 1300;

/** The live-price identifier of a non-US peer: ISIN plus currency. */
const peerIsinKey = (row: any) => `${row.isin}${row.peerCurrency}`;

const StockPeers: FC<PropsTypes> = ({ stock }) => {
    const theme = useAtomValue(themeState);
    const user = useAtomValue(userState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const textColor = useAtomValue(textColorState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const { ortexWebSocket } = useContext(Context);

    const [filters, setFilters] = useState<JSX.Element>();
    const [mainStockPrice, setMainStockPrice] = useState<any>(stock.last_close);
    const [stockDiffPercentage, setStockDiffPercentage] = useState<any>(isFinite(parseFloat(mainStockPrice)) ? priceDiffPercentage(stock.last_close, parseFloat(mainStockPrice)) : NaN);

    const peerLastClosePricesRef = useRef<any>({});
    const stockDiffPercentageRef = useRef<number>(stockDiffPercentage);
    const rowDiffSettersRef = useRef<{ [key: string]: (diff: string[]) => void }>({});
    const peerLivePricesRef = useRef<{ [ticker: string]: number }>({});
    const peerDiffValuesRef = useRef<{ [ticker: string]: string }>({});

    const isUserDataReady = user?.email;

    const [peersNote, setPeersNote] = useState<string | null>(null);
    const [peersNoteLoading, setPeersNoteLoading] = useState(true);
    const [peersNoteObfuscated, setPeersNoteObfuscated] = useState(false);

    useEffect(() => {
        setPeersNote(null);
        setPeersNoteLoading(true);
        getPeersNote(stock.company_id).then(({ note, obfuscated }) => {
            setPeersNote(note);
            setPeersNoteObfuscated(!!obfuscated);
            setPeersNoteLoading(false);
        });
    }, [stock]);

    const [peersMeta, setPeersMeta] = useState<PeersMeta | null>(null);
    const [topPeer, setTopPeer] = useState<PeerRow | null>(null);
    const { series, toggle, ensure, renderIcon } = useChartSeriesToggle(stock.tradingitem_id);

    useEffect(() => {
        setPeersMeta(null);
        setTopPeer(null);
    }, [stock.tradingitem_id]);

    // The peer average is a chart line like any peer's: on by default, and its row's button takes it
    // off and puts it back. No peers, no line.
    const loadAverageLine = useCallback(async (): Promise<TimeSeries[]> => {
        const { series: data } = await getPeerAverageSeries(stock.tradingitem_id);
        return data?.length ? [{ id: AVERAGE_KEY, title: "Peer average", type: "line", data, precision: 2 }] : [];
    }, [stock.tradingitem_id]);
    const loadAverageLineRef = useRef(loadAverageLine);
    loadAverageLineRef.current = loadAverageLine;
    useEffect(() => {
        ensure(AVERAGE_KEY, loadAverageLine);
    }, [ensure, loadAverageLine]);

    // Side by side when the list keeps GRID_MIN_WIDTH at 75% of the row; measured, so it follows the
    // window and the sidebar.
    const layoutRef = useRef<HTMLDivElement>(null);
    const [sideBySide, setSideBySide] = useState(false);
    useEffect(() => {
        const element = layoutRef.current;
        if (!element || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(([entry]) => setSideBySide(entry.contentRect.width * 0.75 >= GRID_MIN_WIDTH));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    // Memoized: the grid refetches whenever `data` changes identity.
    const gridData = useMemo(() => ({ tradingitem_id: stock.tradingitem_id, source: PEER_SOURCE }), [stock.tradingitem_id]);

    // Wraps the API call so the page learns which set was served and its top peer, which the chart
    // draws without a second request. State setters are stable, so the grid keeping an old copy of
    // this function is harmless.
    const fetchPeers = useCallback(async (params: ListQueryParams) => {
        try {
            const res = await getStockPeers(params);
            if (params.page === 1 && !params.next_page_url) {
                setPeersMeta(res.meta ?? null);
                setTopPeer(res.rows?.[0] ?? null);
            }
            return res;
        } finally {
            setFirstPageLoaded(true);
        }
    }, []);

    // Until the list has drawn its first page it holds about its loaded height, so the chart below
    // does not start in view and get pushed off when the rows land (CLS). The hold lets go once the
    // grid has grown past it, or has stopped changing size: a short list then takes its own height.
    const gridBoxRef = useRef<HTMLDivElement>(null);
    const [firstPageLoaded, setFirstPageLoaded] = useState(false);
    const [gridSettled, setGridSettled] = useState(false);
    useEffect(() => {
        setFirstPageLoaded(false);
        setGridSettled(false);
    }, [stock.tradingitem_id]);
    useEffect(() => {
        const box = gridBoxRef.current;
        if (!firstPageLoaded || gridSettled || !box) return;
        if (typeof ResizeObserver === "undefined") return setGridSettled(true);
        const reserved = parseFloat(getComputedStyle(box).minHeight) || 0;
        let timer = setTimeout(() => setGridSettled(true), 2500);
        const observer = new ResizeObserver(() => {
            const grid = box.querySelector(".MuiDataGrid-root");
            if (grid && grid.getBoundingClientRect().height >= reserved) return setGridSettled(true);
            clearTimeout(timer);
            timer = setTimeout(() => setGridSettled(true), 1500);
        });
        const grid = box.querySelector(".MuiDataGrid-root");
        observer.observe(grid ?? box);
        return () => {
            clearTimeout(timer);
            observer.disconnect();
        };
    }, [firstPageLoaded, gridSettled]);

    // Show on Chart. The grid keeps the first render's copy of this object, so it must not read
    // render-time state; `toggle` and `renderIcon` work from refs (see useChartSeriesToggle).
    const chartButton = useMemo(
        () => ({
            onClick: (value: number | string, row: any) => {
                // The stock is always on the chart; the peer average toggles like a peer.
                if (row?.rowKind === "average") return toggle(AVERAGE_KEY, () => loadAverageLineRef.current());
                if (isPinnedRow(row)) return;
                const tradingitemId = Number(row?.peerTradingitemId ?? value);
                if (!tradingitemId) return;
                return toggle(String(tradingitemId), () => loadPeerLine(tradingitemId, row?.ticker));
            },
            renderIcon: (value: number | string, row: any) => {
                if (row?.rowKind === "average") return renderIcon(AVERAGE_KEY);
                return isPinnedRow(row) ? null : renderIcon(String(row?.peerTradingitemId ?? value));
            },
        }),
        [toggle, renderIcon]
    );

    // The closest peer goes on the chart by default, through the same toggle as the button, so its
    // row shows as on and a click removes it. Once per stock: a later refetch (paging, sorting) must
    // not put back a line the user has taken off.
    const autoAddedForRef = useRef<number | null>(null);
    useEffect(() => {
        const tradingitemId = Number(topPeer?.peerTradingitemId);
        if (!tradingitemId || autoAddedForRef.current === stock.tradingitem_id) return;
        autoAddedForRef.current = stock.tradingitem_id;
        ensure(String(tradingitemId), () => loadPeerLine(tradingitemId, topPeer?.ticker));
    }, [topPeer, stock.tradingitem_id, ensure]);

    const getWebSocketMessagesLive = (row) => {
        if (usExchanges.includes(row.exchangeSymbol || row.exchangesymbol || row.exchange)) {
            return [
                { type: "subscribe", identifier: row["ticker"] },
                { type: "last_trades_history_w_ticker", identifier: row["ticker"] },
            ];
        } else if (row.isin && row.peerCurrency) {
            return [
                { type: "subscribe", identifier: peerIsinKey(row) },
                { type: "last_trades_history_w_ticker", identifier: peerIsinKey(row) },
            ];
        } else {
            return [];
        }
    };

    useEffect(() => {
        if (!ortexWebSocket) return;

        const handleMessage = ({ data }) => {
            const parsedData = JSON.parse(data);

            if (parsedData?.LT?.[parsedData?.LT.length - 1] && stock.ticker == parsedData?.ticker) {
                const newPrice = parsedData?.LT[parsedData?.LT.length - 1][0].c;

                setMainStockPrice(newPrice);

                const newStockDiff = isFinite(parseFloat(newPrice)) ? priceDiffPercentage(stock.last_close, parseFloat(newPrice)) : NaN;

                setStockDiffPercentage(newStockDiff);
            } else if (parsedData?.T?.i === stock.ticker) {
                const newPrice = parsedData?.T?.C?.c;
                setMainStockPrice(newPrice);
                const newStockDiff = isFinite(parseFloat(newPrice)) ? priceDiffPercentage(stock.last_close, parseFloat(newPrice)) : NaN;
                setStockDiffPercentage(newStockDiff);
            }
        };

        ortexWebSocket.addEventListener("message", handleMessage);
        return () => ortexWebSocket.removeEventListener("message", handleMessage);
    }, [ortexWebSocket, stock]);

    useEffect(() => {
        stockDiffPercentageRef.current = stockDiffPercentage;
    }, [stockDiffPercentage]);

    // The summary sits over the list, or over the chart when the chart is beside the list.
    const noteCard = (
        <div className={styles.noteCard}>
            <Typography variant="caption" className={styles.noteCardLabel} sx={{ color: textColor }}>
                AI Summary
            </Typography>
            <ClampedNote>
                {peersNoteLoading || peersNote === "" ? (
                    <Typography variant="body2" className={styles.noteCardGenerating} sx={{ color: textColor }}>
                        Generating AI summary of the Peer data...
                    </Typography>
                ) : (
                    <Typography variant="body2" className={`${styles.noteCardContent} ${peersNoteObfuscated ? "obfuscated" : ""}`} sx={{ color: textColor }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={markdownHeadingComponents}>
                            {peersNote}
                        </ReactMarkdown>
                    </Typography>
                )}
            </ClampedNote>
        </div>
    );

    return (
        <div ref={layoutRef} className={classNames(styles[theme], styles.StockPeers, sideBySide && styles.sideBySide)}>
            <div className={styles.table}>
                {!sideBySide && noteCard}
                <div className={styles.tableModule}>
                    <Module title="Peers" help={{ moduleId: "peers", modulePage: "V2_stock_peers" }} allowFullscreen filters={filters}>
                        <div ref={gridBoxRef} className={gridSettled ? undefined : styles.gridBoxReserved}>
                            {isUserDataReady && (
                                <DataGrid
                                    id={stock.tradingitem_id}
                                    data={gridData}
                                    setFilters={setFilters}
                                    tableId={"stock-peers"}
                                    autoHeight
                                    extraPinnedTopRows={peersMeta?.pinnedRows}
                                    fetcher={fetchPeers}
                                    liveData={[
                                        {
                                            message: (row) => getWebSocketMessagesLive(row),
                                            onMessage: (data, name, row, setStockAndPeerDiff) => {
                                                let peerDiffPercentage = NaN;

                                                if (data.ticker === row.ticker || data.ticker === peerIsinKey(row)) {
                                                    const lastClosePrice = row.peerLastClose;
                                                    const newClosePrice = data?.["LT"]?.at(-1)?.[0]?.c;

                                                    const formattedNewClosePrice = newClosePrice?.toFixed?.(2);

                                                    peerDiffPercentage = isFinite(parseFloat(formattedNewClosePrice)) ? priceDiffPercentage(lastClosePrice, parseFloat(formattedNewClosePrice)) : NaN;

                                                    if (!isFinite(peerDiffPercentage)) return;

                                                    peerLivePricesRef.current[row.ticker] = parseFloat(newClosePrice);

                                                    // Business peers show the peer's own move today, not its move against the stock.
                                                    const diff = peerDiffPercentage.toFixed(2);
                                                    peerDiffValuesRef.current[row.ticker] = diff;

                                                    setStockAndPeerDiff([diff]);

                                                    return diff;
                                                }

                                                if (data?.["T"]?.["i"] === row.ticker || data?.["T"]?.["i"] === peerIsinKey(row)) {
                                                    const peerPrice = data?.["T"]?.["C"]?.["c"];
                                                    const lastClosePrice = row.peerLastClose;
                                                    const formattedPeerPrice = peerPrice?.toFixed?.(2);
                                                    const peerDiffPercentage = isFinite(parseFloat(peerPrice)) ? priceDiffPercentage(lastClosePrice, parseFloat(formattedPeerPrice)) : NaN;

                                                    if (!isFinite(peerDiffPercentage)) return;

                                                    // ✅ Store latest live peer price
                                                    peerLivePricesRef.current[row.ticker] = parseFloat(peerPrice);

                                                    const diff = peerDiffPercentage.toFixed(2);
                                                    peerDiffValuesRef.current[row.ticker] = diff;

                                                    setStockAndPeerDiff([diff]);

                                                    return diff;
                                                }
                                            },
                                            // Before (or without) a live price, the move to the last close from the API.
                                            initialValue: (row) => peerDiffValuesRef.current[row.ticker] ?? (typeof row.oneDay === "number" && Number.isFinite(row.oneDay) ? row.oneDay.toFixed(2) : null),
                                            websocket: null,
                                        },
                                    ]}
                                    button={chartButton}
                                />
                            )}
                        </div>
                    </Module>
                </div>
            </div>
            <div className={styles.chart}>
                {sideBySide && noteCard}
                <Module title={`${stock.exchangesymbol}:${stock.ticker} Business Peers`} help={{ moduleId: "peers_chart", modulePage: "V2_stock_peers" }} allowFullscreen noPadding={{ source: "body", type: "mobile" }} feedback>
                    <CorrelatedPeersChart stock={stock} extraSeries={series} drawTopPeer={false} />
                </Module>
            </div>
        </div>
    );
};

export default StockPeers;
